import React, { useState } from 'react';
import { API_BASE_URL, enhancedFetch } from '../services/api';

const RewardQuery = ({ token, onUnauthorized }) => {
  const [nodeId, setNodeId] = useState('');
  const [dailyRewards, setDailyRewards] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  
  // 查询7天收益数据
  const query7DayRewards = async (id) => {
    if (!id) {
      setError('请输入节点号');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 使用新的后端路由/7day-rewards/，传递validator_indices参数
      const response = await enhancedFetch(
        `${API_BASE_URL}/scripts/7day-rewards/?validator_indices=${id}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        onUnauthorized
      );

      if (!response.ok) {
        throw new Error(`查询失败: ${response.status}`);
      }

      const data = await response.json();
      
      // 处理收益数据，适配后端新接口的返回格式
      const dailyRewardData = data.rewards;
      
      // 将每日收益转换为适合显示的格式
      const formattedData = {};
      Object.entries(dailyRewardData).forEach(([nodeId, rewards]) => {
        rewards["7day"].forEach((reward,_index) => {
          if (!formattedData[nodeId]) {
            formattedData[nodeId] = {};
          }
          const date = reward["date"];
          const value = reward["reward"];
          formattedData[nodeId][date] = parseFloat(value);
        });
      });
      
      setDailyRewards(formattedData);
      
      // 添加到历史记录
      const newHistoryItem = {
        id: Date.now(),
        nodeId: id,
        timestamp: new Date().toLocaleString()
      };
      
      // 限制历史记录数量为20条
      setHistory(prev => {
        const updated = [newHistoryItem, ...prev.filter(item => item.nodeId !== id)].slice(0, 20);
        return updated;
      });
      
    } catch (err) {
      console.error('查询7天收益失败:', err);
      setError('查询7天收益失败: ' + err.message);
      setDailyRewards({});
    } finally {
      setLoading(false);
    }
  };
  
  // 从localStorage加载历史记录
  React.useEffect(() => {
    const savedHistory = localStorage.getItem('rewardQueryHistory');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error('解析历史记录失败:', e);
      }
    }
  }, []);

  // 保存历史记录到localStorage
  React.useEffect(() => {
    if(history.length>0){
      localStorage.setItem('rewardQueryHistory', JSON.stringify(history));
    }
  }, [history]);

  // 查询奖励数据
  const queryRewards = async (id) => {
    await query7DayRewards(id);
  };

  // 选择历史记录
  const selectFromHistory = (nodeId) => {
    setNodeId(nodeId);
    queryRewards(nodeId);
  };

  // 清除历史记录
  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('rewardQueryHistory');
  };

  // 处理表单提交
  const handleSubmit = (e) => {
    e.preventDefault();
    queryRewards(nodeId);
  };

  // 渲染7天收益柱状图
  const render7DayBarChart = () => {
    if (Object.keys(dailyRewards).length === 0) return null;

    // 获取所有日期
    const allDates = [];
    Object.values(dailyRewards).forEach(rewards => {
      Object.keys(rewards).forEach(date => {
        if (!allDates.includes(date)) {
          allDates.push(date);
        }
      });
    });
    
    // 按日期排序
    allDates.sort();
    
    // 计算每个节点的总收益
    const totalRewards = {};
    Object.entries(dailyRewards).forEach(([nodeId, rewards]) => {
      totalRewards[nodeId] = Object.values(rewards).reduce((sum, val) => sum + val, 0);
    });
    
    // 按总收益排序节点
    const sortedNodeIds = Object.keys(totalRewards).sort((a, b) => totalRewards[b] - totalRewards[a]);
    
    // 计算最大收益值用于设置图表高度
    let maxRewardValue = 0;
    Object.values(dailyRewards).forEach(rewards => {
      Object.values(rewards).forEach(value => {
        if (value > maxRewardValue) maxRewardValue = value;
      });
    });
    
    // 为不同节点定义不同颜色
    const nodeColors = [
      '#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', 
      '#00BCD4', '#FFC107', '#795548', '#607D8B', '#E91E63'
    ];
    
    return (
      <div className="reward-chart card">
        <h3>7天收益图表</h3>
        <div className="chart-container improved-chart">
          <div className="chart-y-axis">
            <div className="y-axis-label">收益</div>
          </div>
          <div className="chart-content">
            <div className="chart-grid">
              {/* Y轴网格线和标签 */}
              {[0, 0.25, 0.5, 0.75, 1].map(ratio => (
                <div key={ratio} className="grid-line">
                  <span className="grid-label">
                    {(maxRewardValue * ratio).toFixed(2)}
                  </span>
                  <div className="grid-line-horizontal"></div>
                </div>
              ))}
            </div>
            <div className="chart-bars">
              {/* 按日期分组，每个日期显示所有节点的柱状图 */}
              {allDates.map(date => (
                <div key={date} className="chart-date-group">
                  <div className="chart-node-bars">
                    {sortedNodeIds.map((nodeId, nodeIndex) => (
                      <div 
                        key={`${date}-${nodeId}`} 
                        className="chart-bar-container"
                        title={`节点 ${nodeId} 在 ${date} 的收益: ${(dailyRewards[nodeId][date] || 0).toFixed(6)}`}
                      >
                        <div className="chart-bar-wrapper">
                          <div 
                            className="chart-bar" 
                            style={{ 
                              height: maxRewardValue ? `${(dailyRewards[nodeId][date] || 0 / maxRewardValue) * 100}%` : '0%',
                              backgroundColor: nodeColors[nodeIndex % nodeColors.length]
                            }}
                          >
                            <span className="reward-value">{(dailyRewards[nodeId][date] || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="chart-date-label">{date}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* 图例 */}
        <div className="chart-legend">
          {sortedNodeIds.map((nodeId, index) => (
            <div key={nodeId} className="legend-item">
              <div 
                className="legend-color" 
                style={{ backgroundColor: nodeColors[index % nodeColors.length] }}
              ></div>
              <span className="legend-label">{nodeId}</span>
            </div>
          ))}
        </div>
        
        {/* 数据表格 */}
        <div className="reward-table-container">
          <h4>7天收益数据表</h4>
          <div className="reward-table-wrapper">
            {/* 按日期分组显示数据表 */}
            {allDates.map(date => (
              <div key={date} className="date-group">
                <h5>{date} 收益详情</h5>
                <table className="reward-table">
                  <thead>
                    <tr>
                      <th>节点号</th>
                      <th>收益值</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedNodeIds.map(nodeId => (
                      <tr key={`${nodeId}-${date}`}>
                        <td>{nodeId}</td>
                        <td>{(dailyRewards[nodeId][date] || 0).toFixed(6)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>7天节点收益查询</h2>
        <p>查询并查看节点收益信息</p>
      </div>
      
      <form onSubmit={handleSubmit} className="reward-query-form">
        <div className="form-group">
          <label htmlFor="nodeId">节点号:</label>
          <input
            type="text"
            id="nodeId"
            value={nodeId}
            onChange={(e) => setNodeId(e.target.value)}
            placeholder="请输入节点号，多个节点号用逗号分隔"
            className="form-control"
          />
        </div>
        
        <button 
          type="submit" 
          disabled={loading}
          className="primary-button"
        >
          {loading ? (
            <>
              <span className="spinner"></span> 查询中...
            </>
          ) : '查询收益'}
        </button>
      </form>

      {error && (
        <div className="error-message">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <span className="error-text">{error}</span>
            <button className="close-button" onClick={() => setError('')}>×</button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="query-history card">
          <div className="history-header">
            <h3>查询历史</h3>
            <button onClick={clearHistory} className="secondary-button btn-sm">
              清除历史
            </button>
          </div>
          <div className="history-list">
            {history.map((item) => (
              <div 
                key={item.id} 
                className="history-item"
                onClick={() => selectFromHistory(item.nodeId)}
              >
                <span className="node-id">{item.nodeId}</span>
                <span className="timestamp">{item.timestamp}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 标签页切换 */}
      <div className="tab-container">
        <div className="tab-content">
          <div className="chart-container-wrapper">
            {render7DayBarChart()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardQuery;